import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Mic, Square, Loader2, Trash2, Check, X, Sparkles, Pencil,
  ClipboardList, PiggyBank, StickyNote, BookOpen, Calendar
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { playSaveChime, playMicStart, playMicStop, playError } from "@/lib/sound";

// Visual icons / labels per intent target
const TARGET_META = {
  task:        { label: "Compito",       icon: ClipboardList, color: "text-blue-600",    bg: "bg-blue-50",    border: "border-blue-200" },
  expense:     { label: "Spesa",         icon: PiggyBank,     color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-200" },
  note:        { label: "Nota",          icon: StickyNote,    color: "text-amber-600",   bg: "bg-amber-50",   border: "border-amber-200" },
  diary:       { label: "Diario",        icon: BookOpen,      color: "text-cyan-600",    bg: "bg-cyan-50",    border: "border-cyan-200" },
  appointment: { label: "Appuntamento",  icon: Calendar,      color: "text-purple-600",  bg: "bg-purple-50",  border: "border-purple-200" },
};

const SmartVoiceCommand = () => {
  const [open, setOpen] = useState(false);
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [preview, setPreview] = useState(null); // { transcript, intent }

  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const cancelledRef = useRef(false);

  const resetState = () => {
    setRecording(false);
    setProcessing(false);
    setSaving(false);
    setElapsed(0);
    setPreview(null);
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm;codecs=opus" });
      mediaRef.current = mr;
      chunksRef.current = [];
      cancelledRef.current = false;

      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        if (cancelledRef.current) return;
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setProcessing(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "command.webm");
          const res = await api.post("/voice-command/process", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          setPreview(res.data);
          playSaveChime();
        } catch (err) {
          playError();
          toast.error(err.response?.data?.detail || "Errore nell'analisi vocale");
          setOpen(false);
          resetState();
        } finally {
          setProcessing(false);
        }
      };

      mr.start(250);
      playMicStart();
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((p) => p + 1), 1000);
    } catch {
      toast.error("Impossibile accedere al microfono");
    }
  };

  const stopRec = () => {
    if (mediaRef.current?.state !== "inactive") mediaRef.current?.stop();
    clearInterval(timerRef.current);
    setRecording(false);
    playMicStop();
  };

  const cancelRec = () => {
    cancelledRef.current = true;
    if (mediaRef.current?.state !== "inactive") mediaRef.current?.stop();
    clearInterval(timerRef.current);
    resetState();
    toast("Registrazione cestinata");
  };

  // Stop & release mic if dialog closed during recording
  useEffect(() => {
    if (!open && recording) cancelRec();
    if (!open) resetState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Cleanup on unmount
  useEffect(() => () => clearInterval(timerRef.current), []);

  const handleConfirmSave = async () => {
    if (!preview?.intent) return;
    const intent = preview.intent;
    setSaving(true);
    try {
      const payload = { ...intent, transcript: preview.transcript };
      const res = await api.post("/voice-command/execute", payload);
      playSaveChime();
      toast.success(res.data.summary || "Salvato!");
      setOpen(false);
      resetState();
    } catch (err) {
      playError();
      toast.error(err.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const intent = preview?.intent;
  const target = intent?.target;
  const meta = TARGET_META[target];
  const isUnknown = !meta || target === "unknown";

  // Update a field inside preview.intent (keeps everything else intact)
  const updateIntent = (field, value) => {
    setPreview((prev) => prev ? ({
      ...prev,
      intent: { ...prev.intent, [field]: value },
    }) : prev);
  };

  const previewEditor = () => {
    if (!intent) return null;
    if (isUnknown) {
      return (
        <p className="text-sm text-muted-foreground">
          Non sono riuscita a capire come elaborare il tuo audio.<br />
          <span className="text-xs italic">{intent.summary || "Riprova parlando più chiaramente."}</span>
        </p>
      );
    }
    if (target === "task") {
      return (
        <div className="space-y-2.5">
          <div>
            <Label className="text-[11px] text-muted-foreground">Titolo del compito</Label>
            <Input
              value={intent.title || ""}
              onChange={(e) => updateIntent("title", e.target.value)}
              className="h-8 text-sm mt-0.5"
              data-testid="smart-voice-edit-title"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[11px] text-muted-foreground">Categoria</Label>
              <Input
                value={intent.custom_domain || intent.domain || ""}
                onChange={(e) => updateIntent("custom_domain", e.target.value)}
                placeholder="es. lavoro, casa"
                className="h-8 text-sm mt-0.5"
                data-testid="smart-voice-edit-domain"
              />
            </div>
            <div>
              <Label className="text-[11px] text-muted-foreground">Scadenza (opz.)</Label>
              <Input
                type="date"
                value={intent.due_date || ""}
                onChange={(e) => updateIntent("due_date", e.target.value || null)}
                className="h-8 text-sm mt-0.5"
                data-testid="smart-voice-edit-due"
              />
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
            <input
              type="checkbox"
              checked={!!intent.is_urgent}
              onChange={(e) => updateIntent("is_urgent", e.target.checked)}
              data-testid="smart-voice-edit-urgent"
            />
            Urgente
          </label>
        </div>
      );
    }
    if (target === "expense") {
      return (
        <div className="space-y-2.5">
          <div>
            <Label className="text-[11px] text-muted-foreground">Descrizione</Label>
            <Input
              value={intent.description || ""}
              onChange={(e) => updateIntent("description", e.target.value)}
              className="h-8 text-sm mt-0.5"
              data-testid="smart-voice-edit-desc"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[11px] text-muted-foreground">Importo (€)</Label>
              <Input
                type="number"
                step="0.01"
                value={intent.amount ?? ""}
                onChange={(e) => updateIntent("amount", parseFloat(e.target.value) || 0)}
                className="h-8 text-sm mt-0.5"
                data-testid="smart-voice-edit-amount"
              />
            </div>
            <div>
              <Label className="text-[11px] text-muted-foreground">Data</Label>
              <Input
                type="date"
                value={intent.date || ""}
                onChange={(e) => updateIntent("date", e.target.value)}
                className="h-8 text-sm mt-0.5"
                data-testid="smart-voice-edit-date"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[11px] text-muted-foreground">Categoria</Label>
              <select
                value={intent.category || "I"}
                onChange={(e) => updateIntent("category", e.target.value)}
                className="w-full h-8 text-sm mt-0.5 rounded-md border bg-background px-2"
                data-testid="smart-voice-edit-category"
              >
                <option value="I">I — Indispensabili</option>
                <option value="A">A — Accessorie</option>
                <option value="X">X — Imprevisti</option>
              </select>
            </div>
            <div>
              <Label className="text-[11px] text-muted-foreground">Sottocategoria</Label>
              <Input
                value={intent.subcategory || ""}
                onChange={(e) => updateIntent("subcategory", e.target.value)}
                placeholder="es. spesa, benzina"
                className="h-8 text-sm mt-0.5"
                data-testid="smart-voice-edit-subcat"
              />
            </div>
          </div>
        </div>
      );
    }
    if (target === "note") {
      return (
        <div className="space-y-2.5">
          <div>
            <Label className="text-[11px] text-muted-foreground">Titolo (opzionale)</Label>
            <Input
              value={intent.title || ""}
              onChange={(e) => updateIntent("title", e.target.value)}
              className="h-8 text-sm mt-0.5"
              data-testid="smart-voice-edit-note-title"
            />
          </div>
          <div>
            <Label className="text-[11px] text-muted-foreground">Contenuto</Label>
            <Textarea
              value={intent.content || ""}
              onChange={(e) => updateIntent("content", e.target.value)}
              rows={4}
              className="text-sm mt-0.5"
              data-testid="smart-voice-edit-note-content"
            />
          </div>
        </div>
      );
    }
    if (target === "diary") {
      const contentField = intent.free_notes !== undefined ? "free_notes" : "content";
      return (
        <div className="space-y-2.5">
          <div>
            <Label className="text-[11px] text-muted-foreground">Data</Label>
            <Input
              type="date"
              value={intent.date || ""}
              onChange={(e) => updateIntent("date", e.target.value)}
              className="h-8 text-sm mt-0.5"
              data-testid="smart-voice-edit-diary-date"
            />
          </div>
          <div>
            <Label className="text-[11px] text-muted-foreground">Pensiero del giorno</Label>
            <Textarea
              value={intent[contentField] || ""}
              onChange={(e) => updateIntent(contentField, e.target.value)}
              rows={5}
              className="text-sm mt-0.5"
              data-testid="smart-voice-edit-diary-content"
            />
          </div>
        </div>
      );
    }
    if (target === "appointment") {
      return (
        <div className="space-y-2.5">
          <div>
            <Label className="text-[11px] text-muted-foreground">Titolo</Label>
            <Input
              value={intent.title || ""}
              onChange={(e) => updateIntent("title", e.target.value)}
              className="h-8 text-sm mt-0.5"
              data-testid="smart-voice-edit-appt-title"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[11px] text-muted-foreground">Data</Label>
              <Input
                type="date"
                value={intent.date || ""}
                onChange={(e) => updateIntent("date", e.target.value)}
                className="h-8 text-sm mt-0.5"
                data-testid="smart-voice-edit-appt-date"
              />
            </div>
            <div>
              <Label className="text-[11px] text-muted-foreground">Ora (opz.)</Label>
              <Input
                type="time"
                value={intent.time || ""}
                onChange={(e) => updateIntent("time", e.target.value || null)}
                className="h-8 text-sm mt-0.5"
                data-testid="smart-voice-edit-appt-time"
              />
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        className="h-8 px-2.5 sm:px-3 text-muted-foreground hover:text-rose-600 border-rose-200 hover:border-rose-300 hover:bg-rose-50"
        data-testid="smart-voice-cmd-btn"
        title="Comando vocale: aggiunge in compiti, spese, note, diario, appuntamenti"
      >
        <Sparkles className="w-4 h-4 text-rose-500 sm:mr-1" />
        <span className="hidden sm:inline text-xs">Voce magica</span>
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="smart-voice-cmd-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-rose-500" />
              Comando vocale intelligente
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">

            {/* RECORDER STATE */}
            {!preview && (
              <div className="bg-gradient-to-br from-rose-50 to-pink-50 border border-rose-200 rounded-xl p-4">
                {processing ? (
                  <div className="flex items-center justify-center gap-3 py-3">
                    <Loader2 className="w-5 h-5 animate-spin text-rose-500" />
                    <span className="text-sm">Sto pensando…</span>
                  </div>
                ) : recording ? (
                  <div className="flex flex-col items-center gap-3">
                    <button
                      onClick={stopRec}
                      className="w-16 h-16 rounded-full bg-red-500 animate-pulse flex items-center justify-center shadow-lg"
                      data-testid="smart-voice-stop-btn"
                      aria-label="Invia comando vocale"
                    >
                      <Square className="w-6 h-6 text-white fill-white" />
                    </button>
                    <span className="text-sm font-mono text-red-600">
                      {Math.floor(elapsed / 60)}:{(elapsed % 60).toString().padStart(2, "0")}
                    </span>
                    <p className="text-xs text-muted-foreground">Tocca il quadrato per inviare</p>
                    <button
                      onClick={cancelRec}
                      className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-red-600"
                      data-testid="smart-voice-cancel-btn"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Cestina
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-3">
                    <button
                      onClick={startRec}
                      className="w-16 h-16 rounded-full bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center shadow-lg hover:scale-105 active:scale-95 transition-transform"
                      data-testid="smart-voice-record-btn"
                      aria-label="Inizia comando vocale"
                    >
                      <Mic className="w-7 h-7 text-white" />
                    </button>
                    <p className="text-xs text-muted-foreground text-center max-w-[260px]">
                      Es: <em>"Aggiungi nei compiti Andar: chiamare segretaria"</em><br />
                      <em>"Spesa 30 euro al supermercato"</em>
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* PREVIEW STATE */}
            {preview && (
              <div className="space-y-3" data-testid="smart-voice-preview">
                <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">
                  Hai detto:
                </div>
                <div className="bg-muted/40 rounded-lg p-2.5 italic text-sm">
                  “{preview.transcript}”
                </div>

                <div className={`rounded-xl border ${meta?.border || "border-gray-200"} ${meta?.bg || "bg-gray-50"} p-3 space-y-2.5`}>
                  <div className="flex items-center justify-between gap-2">
                    {meta ? (
                      <div className="flex items-center gap-2">
                        <div className={`w-7 h-7 rounded-full bg-white flex items-center justify-center shrink-0 border ${meta.border}`}>
                          <meta.icon className={`w-3.5 h-3.5 ${meta.color}`} />
                        </div>
                        <span className={`text-[11px] uppercase tracking-wider font-semibold ${meta.color}`}>
                          {meta.label}
                        </span>
                      </div>
                    ) : (
                      <span className="text-[11px] uppercase tracking-wider font-semibold text-gray-500">
                        Non riconosciuto
                      </span>
                    )}
                    {!isUnknown && (
                      <span className="flex items-center gap-1 text-[10px] text-muted-foreground italic">
                        <Pencil className="w-3 h-3" /> Modifica prima di salvare
                      </span>
                    )}
                  </div>
                  {previewEditor()}
                </div>

                <div className="flex gap-2 pt-1">
                  {!isUnknown ? (
                    <Button
                      onClick={handleConfirmSave}
                      disabled={saving}
                      className="flex-1 bg-rose-500 hover:bg-rose-600 text-white"
                      data-testid="smart-voice-confirm-btn"
                    >
                      {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Check className="w-4 h-4 mr-1.5" />}
                      Conferma e salva
                    </Button>
                  ) : null}
                  <Button
                    onClick={() => { setPreview(null); resetState(); }}
                    variant="outline"
                    disabled={saving}
                    className="flex-1"
                    data-testid="smart-voice-retry-btn"
                  >
                    <X className="w-4 h-4 mr-1.5" />
                    {isUnknown ? "Riprova" : "Annulla"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SmartVoiceCommand;
